import { FC } from 'react';
import { useRecordContext, useTranslate } from 'react-admin';
import { Box, Stack, Typography } from '@mui/material';

import { UpdateChannel } from 'types/UpdateChannel';
import { useHardwareFilterId } from './hardwareFilter';

interface UpdateChannelCurrentReleaseFieldProps {
  /** Read by `Datagrid` for the column header. */
  label?: string;
  /** Read by `Datagrid`. The column is computed, so pass `false`. */
  sortable?: boolean;
}

interface ReleaseLine {
  revisionId: number;
  revisionName: string;
  releaseName: string | null;
}

/**
 * The channel's current release for the hardware the list is filtered on (TVR-2382): one line
 * per revision of that hardware, "<revision name> <release name>", sorted by revision name.
 * Without a hardware filter it shows only a revision count; the details live on the Show page.
 *
 * It reads `record.current_releases` only. The legacy `software_release_id` is the latest
 * release across ALL of the channel's revisions, which is exactly the misleading value this
 * column replaces, so it is never used as a fallback.
 *
 * Needs a ListContext: it reads the filter through `useHardwareFilterId`, whose `useListContext`
 * throws outside a list. Render it in a `List`/`Datagrid` (or a `ListContextProvider`), not on a
 * Show or Edit page.
 */
export const UpdateChannelCurrentReleaseField: FC<UpdateChannelCurrentReleaseFieldProps> = () => {
  const record = useRecordContext<UpdateChannel>();
  const translate = useTranslate();
  const filterHardwareId = useHardwareFilterId();

  if (!record) return null;

  const revisions = record.hardware_revisions ?? [];

  if (filterHardwareId === null) {
    return (
      <Typography variant="body2" color="text.secondary">
        {translate('resources.update_channels.list.revision_count', { smart_count: revisions.length })}
      </Typography>
    );
  }

  const revisionNames = new Map(revisions.map((revision) => [revision.hardware_revision_id, revision.name]));
  const lines: ReleaseLine[] = (record.current_releases ?? [])
    .filter((release) => release.hardware_id === filterHardwareId)
    .map((release) => ({
      revisionId: release.hardware_revision_id,
      revisionName: revisionNames.get(release.hardware_revision_id) ?? String(release.hardware_revision_id),
      releaseName: release.software_release_id == null ? null : (release.name ?? '—'),
    }))
    .sort((a, b) => a.revisionName.localeCompare(b.revisionName, undefined, { numeric: true }));

  const notReleased = translate('resources.update_channels.list.not_released');

  // Nothing released for this hardware, including a channel with no revisions of it at all.
  if (lines.every((line) => line.releaseName === null)) {
    return (
      <Typography variant="body2" color="text.secondary">
        {notReleased}
      </Typography>
    );
  }

  return (
    <Stack spacing={0.25}>
      {lines.map((line) => (
        <Typography key={line.revisionId} variant="body2">
          <Box component="span" sx={{ fontWeight: 'bold' }}>
            {line.revisionName}
          </Box>{' '}
          {line.releaseName ?? (
            <Typography component="span" variant="body2" color="text.secondary">
              {notReleased}
            </Typography>
          )}
        </Typography>
      ))}
    </Stack>
  );
};
